import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ShiftCreateInput,
  ShiftDetails,
  ShiftListFilters,
  ShiftListItem,
  ShiftSearchResult,
  ShiftUpdateInput,
} from '../types/shift';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { nextDisplayOrder } from '../utils/displayOrder';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';
import { spreadMinutesFromDurations } from '../utils/workDuration';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const DETAILS_PAGE_SIZE = 100;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function requiredBool(value: unknown, label: string): boolean {
  const parsed = boolOrNull(value);
  if (parsed === null) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function nonNegativeOrZero(value: unknown): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed < 0 || !Number.isInteger(parsed)) return 0;
  return parsed;
}

function formatTimeForApi(value: string): string {
  const trimmed = value.trim();
  if (/^([01]\d|2[0-3]):[0-5]\d$/.test(trimmed)) return `${trimmed}:00`;
  return trimmed;
}

function clockMinutes(value: string): number {
  const [hours, minutes] = formatTimeForApi(value).split(':').map(Number);
  return hours * 60 + minutes;
}

/** Fallback when a stored lunch duration is not returned. */
export function lunchBreakMinutesFromShift(input: {
  shiftStartTime: string;
  shiftEndTime: string;
  crossesMidnight: boolean;
  expectedWorkMinutes: number;
}): number {
  const startMinutes = clockMinutes(input.shiftStartTime);
  let endMinutes = clockMinutes(input.shiftEndTime);
  if (input.crossesMidnight || endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }
  return Math.max(0, endMinutes - startMinutes - input.expectedWorkMinutes);
}

function resolveLunchBreakMinutes(
  row: ObjectValue,
  expectedWorkMinutes: number,
): number {
  const stored = numberOrNull(row.lunchBreakMinutes);
  if (stored != null && stored >= 0) return stored;
  const start = displayTime(row.shiftStartTime) ?? textOrNull(row.shiftStartTime);
  const end = displayTime(row.shiftEndTime) ?? textOrNull(row.shiftEndTime);
  if (!start || !end) return 0;
  return lunchBreakMinutesFromShift({
    shiftStartTime: start,
    shiftEndTime: end,
    crossesMidnight: boolOrNull(row.crossesMidnight) ?? false,
    expectedWorkMinutes,
  });
}

function resolveSpreadMinutes(
  row: ObjectValue,
  expectedWorkMinutes: number,
  lunchBreakMinutes: number,
): number {
  const stored = numberOrNull(row.spreadMinutes);
  if (stored != null && stored >= 0) return stored;
  return spreadMinutesFromDurations(expectedWorkMinutes, lunchBreakMinutes);
}

function displayTime(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  // Prefer HH:mm in the UI when the backend returns HH:mm:ss.
  if (/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(text)) {
    return text.slice(0, 5);
  }
  return text;
}

function normalizeListItem(row: ObjectValue): ShiftListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    return {
      shiftId: requiredId(row.shiftId, 'shiftId'),
      shiftCode: textOrNull(row.shiftCode) ?? String(row.shiftId),
      shiftName: textOrNull(row.shiftName) ?? 'Shift',
      locationId: numberOrNull(row.locationId),
      locationName: textOrNull(row.locationName),
      shiftStartTime: displayTime(row.shiftStartTime),
      shiftEndTime: displayTime(row.shiftEndTime),
      crossesMidnight: boolOrNull(row.crossesMidnight),
      expectedWorkMinutes: numberOrNull(row.expectedWorkMinutes),
      lunchBreakMinutes: numberOrNull(row.lunchBreakMinutes),
      spreadMinutes: numberOrNull(row.spreadMinutes),
      lateGraceMinutes: numberOrNull(row.lateGraceMinutes),
      earlyExitGraceMinutes: numberOrNull(row.earlyExitGraceMinutes),
      isFlexibleShift: boolOrNull(row.isFlexibleShift),
      isDefault: boolOrNull(row.isDefault),
      isActive,
      statusLabel: getMasterDataStatusLabel(
        isActive,
        textOrNull(row.statusCode),
      ),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function normalizeDetails(row: ObjectValue): ShiftDetails {
  const isActive = boolOrNull(row.isActive);
  const expectedWorkMinutes = requiredId(
    row.expectedWorkMinutes,
    'expectedWorkMinutes',
  );
  const lunchBreakMinutes = resolveLunchBreakMinutes(row, expectedWorkMinutes);
  return {
    shiftId: requiredId(row.shiftId, 'shiftId'),
    shiftCode: requiredText(row.shiftCode, 'shiftCode'),
    shiftName: requiredText(row.shiftName, 'shiftName'),
    description: textOrNull(row.description),
    locationId: numberOrNull(row.locationId),
    locationName: textOrNull(row.locationName),
    shiftStartTime:
      displayTime(row.shiftStartTime) ??
      requiredText(row.shiftStartTime, 'shiftStartTime'),
    shiftEndTime:
      displayTime(row.shiftEndTime) ??
      requiredText(row.shiftEndTime, 'shiftEndTime'),
    crossesMidnight: boolOrNull(row.crossesMidnight) ?? false,
    expectedWorkMinutes,
    lunchBreakMinutes,
    spreadMinutes: resolveSpreadMinutes(
      row,
      expectedWorkMinutes,
      lunchBreakMinutes,
    ),
    minimumHalfDayMinutes: requiredId(
      row.minimumHalfDayMinutes,
      'minimumHalfDayMinutes',
    ),
    checkInOpenMinutes: nonNegativeOrZero(row.checkInOpenMinutes),
    checkInCloseMinutes: nonNegativeOrZero(row.checkInCloseMinutes),
    checkOutOpenMinutes: nonNegativeOrZero(row.checkOutOpenMinutes),
    checkOutCloseMinutes: nonNegativeOrZero(row.checkOutCloseMinutes),
    lateGraceMinutes: nonNegativeOrZero(row.lateGraceMinutes),
    earlyExitGraceMinutes: nonNegativeOrZero(row.earlyExitGraceMinutes),
    autoCheckoutEnabled: boolOrNull(row.autoCheckoutEnabled) ?? false,
    autoCheckoutAfterMinutes: numberOrNull(row.autoCheckoutAfterMinutes),
    isFlexibleShift: boolOrNull(row.isFlexibleShift) ?? false,
    isDefault: boolOrNull(row.isDefault) ?? false,
    displayOrder: nonNegativeOrZero(row.displayOrder),
    isActive,
    statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
    rowVersion: requiredId(row.rowVersion, 'rowVersion'),
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: ShiftListFilters = {},
) {
  const params: Record<string, string | number | boolean> = {
    page,
    pageSize,
  };
  const search = filters.search?.trim();
  if (search) params.search = search;
  // Backend shift validator only accepts 1/0/'1'/'0' (not "true"/"false").
  if (filters.isActive === true) {
    params.isActive = 1;
  } else if (filters.isActive === false) {
    params.isActive = 0;
  }
  if (
    filters.locationId != null &&
    Number.isInteger(filters.locationId) &&
    filters.locationId > 0
  ) {
    params.locationId = filters.locationId;
  }
  return params;
}

export async function getShifts(
  options: {
    page?: number;
    pageSize?: number;
    filters?: ShiftListFilters;
  } = {},
): Promise<ShiftSearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.SHIFTS.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is ShiftListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/**
 * No dedicated GET-by-id route. Locate the record through tenant-scoped search
 * pages (same approach the backend uses for ownership checks).
 */
export async function getShiftDetails(shiftId: number): Promise<ShiftDetails> {
  if (!Number.isInteger(shiftId) || shiftId <= 0) {
    throw Object.assign(new Error('shiftId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await apiClient.get<
      Envelope<{
        items?: ObjectValue[];
        pagination?: { totalPages?: number; totalRecords?: number };
      }>
    >(API_ENDPOINTS.SHIFTS.LIST, {
      params: { page, pageSize: DETAILS_PAGE_SIZE },
    });
    const data = response.data.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    const match = items.find(row => numberOrNull(row.shiftId) === shiftId);
    if (match) return normalizeDetails(match);

    const totalRecords = numberOrNull(data.pagination?.totalRecords) ?? 0;
    totalPages =
      numberOrNull(data.pagination?.totalPages) ??
      Math.max(1, Math.ceil(totalRecords / DETAILS_PAGE_SIZE));
    page += 1;
  }

  throw Object.assign(new Error('Shift was not found.'), {
    code: 'SHIFT_NOT_FOUND',
  });
}

export async function createShift(
  input: ShiftCreateInput,
): Promise<ShiftDetails> {
  const existing = await getShifts({ page: 1, pageSize: DETAILS_PAGE_SIZE });
  const body = {
    locationId: input.locationId ?? null,
    shiftCode: input.shiftCode.trim(),
    shiftName: input.shiftName.trim(),
    description: emptyToNull(input.description),
    shiftStartTime: formatTimeForApi(input.shiftStartTime),
    shiftEndTime: formatTimeForApi(input.shiftEndTime),
    crossesMidnight: input.crossesMidnight,
    expectedWorkMinutes: input.expectedWorkMinutes,
    lunchBreakMinutes: input.lunchBreakMinutes,
    spreadMinutes: spreadMinutesFromDurations(
      input.expectedWorkMinutes,
      input.lunchBreakMinutes,
    ),
    minimumHalfDayMinutes: input.minimumHalfDayMinutes,
    checkInOpenMinutes: input.checkInOpenMinutes,
    checkInCloseMinutes: input.checkInCloseMinutes,
    checkOutOpenMinutes: input.checkOutOpenMinutes,
    checkOutCloseMinutes: input.checkOutCloseMinutes,
    lateGraceMinutes: input.lateGraceMinutes,
    earlyExitGraceMinutes: input.earlyExitGraceMinutes,
    autoCheckoutEnabled: input.autoCheckoutEnabled,
    autoCheckoutAfterMinutes: input.autoCheckoutEnabled
      ? input.autoCheckoutAfterMinutes ?? null
      : null,
    isFlexibleShift: input.isFlexibleShift,
    isDefault: input.isDefault,
    displayOrder: nextDisplayOrder([], existing.totalItems),
  };

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.SHIFTS.CREATE,
    body,
  );
  return normalizeDetails({
    ...body,
    isActive: true,
    rowVersion: 1,
    ...(response.data.data ?? {}),
  });
}

export async function updateShift(
  shiftId: number,
  input: ShiftUpdateInput,
): Promise<ShiftDetails> {
  if (!Number.isInteger(shiftId) || shiftId <= 0) {
    throw Object.assign(new Error('shiftId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body = {
    locationId: input.locationId ?? null,
    shiftName: input.shiftName.trim(),
    description: emptyToNull(input.description),
    shiftStartTime: formatTimeForApi(input.shiftStartTime),
    shiftEndTime: formatTimeForApi(input.shiftEndTime),
    crossesMidnight: input.crossesMidnight,
    expectedWorkMinutes: input.expectedWorkMinutes,
    lunchBreakMinutes: input.lunchBreakMinutes,
    spreadMinutes: spreadMinutesFromDurations(
      input.expectedWorkMinutes,
      input.lunchBreakMinutes,
    ),
    minimumHalfDayMinutes: input.minimumHalfDayMinutes,
    checkInOpenMinutes: input.checkInOpenMinutes,
    checkInCloseMinutes: input.checkInCloseMinutes,
    checkOutOpenMinutes: input.checkOutOpenMinutes,
    checkOutCloseMinutes: input.checkOutCloseMinutes,
    lateGraceMinutes: input.lateGraceMinutes,
    earlyExitGraceMinutes: input.earlyExitGraceMinutes,
    autoCheckoutEnabled: input.autoCheckoutEnabled,
    autoCheckoutAfterMinutes: input.autoCheckoutEnabled
      ? input.autoCheckoutAfterMinutes ?? null
      : null,
    isFlexibleShift: input.isFlexibleShift,
    isDefault: input.isDefault,
    displayOrder: input.displayOrder,
    isActive: requiredBool(input.isActive, 'isActive'),
    rowVersion: input.rowVersion,
  };

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.SHIFTS.UPDATE(shiftId),
    body,
  );
  return normalizeDetails({
    shiftId,
    shiftCode: '—',
    ...body,
    ...(response.data.data ?? {}),
  });
}

export async function deactivateShift(item: ShiftDetails): Promise<ShiftDetails> {
  return updateShift(item.shiftId, {
    locationId: item.locationId,
    shiftName: item.shiftName,
    description: item.description,
    shiftStartTime: item.shiftStartTime,
    shiftEndTime: item.shiftEndTime,
    crossesMidnight: item.crossesMidnight,
    expectedWorkMinutes: item.expectedWorkMinutes,
    lunchBreakMinutes: item.lunchBreakMinutes,
    minimumHalfDayMinutes: item.minimumHalfDayMinutes,
    checkInOpenMinutes: item.checkInOpenMinutes,
    checkInCloseMinutes: item.checkInCloseMinutes,
    checkOutOpenMinutes: item.checkOutOpenMinutes,
    checkOutCloseMinutes: item.checkOutCloseMinutes,
    lateGraceMinutes: item.lateGraceMinutes,
    earlyExitGraceMinutes: item.earlyExitGraceMinutes,
    autoCheckoutEnabled: item.autoCheckoutEnabled,
    autoCheckoutAfterMinutes: item.autoCheckoutAfterMinutes,
    isFlexibleShift: item.isFlexibleShift,
    isDefault: item.isDefault,
    displayOrder: item.displayOrder,
    isActive: CONFIGURATION_RECORD_INACTIVE,
    rowVersion: item.rowVersion,
  });
}
